import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, retryDelayMs } from '../../api';
import { useAuthContext } from '../../contexts/AuthContext';
import { heartbeat, release, requestPlay } from '../../services/playback/v1Play';
import { PlaybackError, playbackErrorFromApi } from '../../services/playback/PlaybackError';

/**
 * How many times one play asks again while entitlement is undetermined (a 503 that must never
 * become a paywall). ponytail: a fixed budget; the server's retry_after_s sets the spacing.
 */
export const UNDETERMINED_ATTEMPTS = 5;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/** INTERNAL, RATE_LIMITED and no-response are retried once; any other refusal is final. */
const attemptsFor = error => {
  if (error?.code === 'ENTITLEMENT_UNDETERMINED') {
    return UNDETERMINED_ATTEMPTS;
  }
  if (!(error instanceof ApiError) || error.code === 'INTERNAL' || error.code === 'RATE_LIMITED') {
    return 2;
  }
  return 1;
};

const stateFor = error => {
  if (error?.code === 'LOGIN_REQUIRED' || error?.code === 'SESSION_INVALID') {
    return { status: 'login' };
  }
  if (error?.code === 'NOT_ENTITLED') {
    return { status: 'not_entitled' };
  }
  return { status: 'error', error: playbackErrorFromApi(error) };
};

/**
 * One playback grant per mounted media (bkfc-cms docs/routes/playback.md §8): mint it with
 * `POST /v1/media/{id}/play`, beat `PUT /v1/play/{grant}/heartbeat` at the server's interval
 * (re-read from every ack, paused or not), and release it on unmount, page hide and on demand.
 * A signed-out viewer never reaches `/play`: the gate is here, so no anonymous preview is minted.
 *
 * @param {string|null} mediaId - v1 media id or slug
 * @returns {{status: 'loading'|'login'|'not_entitled'|'ready'|'error', grant?: object,
 *   error?: PlaybackError, release: () => void}}
 */
export function usePlaybackGrant(mediaId) {
  const { isAuthenticated, isLoading } = useAuthContext();
  const [state, setState] = useState({ status: 'loading' });
  // Bumped to mint a fresh grant (new request id): a 404 beat, or a page restored from bfcache.
  const [generation, setGeneration] = useState(0);
  const releaseRef = useRef(() => {});

  useEffect(() => {
    if (isLoading) {
      return undefined;
    }
    if (!mediaId) {
      setState({ status: 'error', error: new PlaybackError('not_found') });
      return undefined;
    }
    if (!isAuthenticated) {
      setState({ status: 'login' });
      return undefined;
    }

    let cancelled = false;
    let grantId = null;
    let released = false;
    let beatTimer = null;
    let intervalS = 0;
    const requestId = globalThis.crypto.randomUUID();

    const releaseGrant = () => {
      clearTimeout(beatTimer);
      if (grantId && !released) {
        released = true;
        release(grantId);
      }
    };
    releaseRef.current = releaseGrant;

    const beat = async () => {
      let nextS = intervalS;
      try {
        const ack = await heartbeat(grantId);
        if (Number(ack?.heartbeat_interval_s) > 0) {
          intervalS = ack.heartbeat_interval_s;
        }
        nextS = intervalS;
      } catch (error) {
        if (error instanceof ApiError && error.code === 'NOT_FOUND') {
          // The grant is closed server-side. Stop beating and mint a new one.
          released = true;
          if (!cancelled) {
            setGeneration(g => g + 1);
          }
          return;
        }
        // A 500 or no response keeps playing: the grace exists for one lost beat.
        if (error?.retryAfterMs) {
          nextS = Math.max(intervalS, error.retryAfterMs / 1000);
        }
      }
      if (!cancelled && !released) {
        beatTimer = setTimeout(beat, nextS * 1000);
      }
    };

    setState({ status: 'loading' });
    (async () => {
      let grant;
      for (let attempt = 0; ; attempt += 1) {
        try {
          grant = await requestPlay(mediaId, requestId);
          break;
        } catch (error) {
          if (cancelled) {
            return;
          }
          if (attempt + 1 >= attemptsFor(error)) {
            setState(stateFor(error));
            return;
          }
          await sleep(error?.retryAfterMs ?? retryDelayMs(attempt, error));
          if (cancelled) {
            return;
          }
        }
      }
      grantId = grant.grant_id;
      if (cancelled) {
        releaseGrant();
        return;
      }
      intervalS = grant.heartbeat_interval_s;
      beatTimer = setTimeout(beat, intervalS * 1000);
      setState({ status: 'ready', grant });
    })();

    const onPageHide = () => releaseGrant();
    const onPageShow = event => {
      if (event.persisted && released) {
        setGeneration(g => g + 1);
      }
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      cancelled = true;
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      releaseGrant();
    };
  }, [mediaId, isAuthenticated, isLoading, generation]);

  const releaseNow = useCallback(() => releaseRef.current(), []);
  return { ...state, release: releaseNow };
}

export default usePlaybackGrant;
